import { useTranslation } from 'react-i18next';

import type { StatistaChartFactsProps } from './StatistaChartFacts.types';

const YEAR = /^\d{4}\*?$/;
const BARE_YEAR = /^\d{4}$/;
const YEAR_MONTH = /^(\d{4})-(\d{2})/;

/**
 * `publishedDate` is whatever string the vendor sent, and Statista dates are overwhelmingly
 * the first of a month. A date-only string parses as UTC midnight, so formatting it in the
 * reader's zone moves it back a day - to the month before for a day-01 date, and to the year
 * before for 1 January. A bare year, and the year and month of a date-only string, are
 * therefore read off the string and formatted in UTC; only genuinely timestamped values go
 * through the reader's zone. Anything unparseable renders as nothing rather than the words
 * "Invalid Date".
 */
const formatPublished = (publishedDate: string | null): string | undefined => {
  if (!publishedDate) return undefined;
  if (BARE_YEAR.test(publishedDate)) return publishedDate;

  const yearMonth = YEAR_MONTH.exec(publishedDate);
  if (yearMonth) {
    const [, year, month] = yearMonth;
    return new Date(Date.UTC(Number(year), Number(month) - 1, 1)).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    });
  }

  const parsed = new Date(publishedDate);
  return Number.isNaN(parsed.getTime())
    ? undefined
    : parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
};

export const StatistaChartFacts = ({ dataset }: StatistaChartFactsProps) => {
  const { t } = useTranslation('analysis');
  const { normalized, unit, publishedDate } = dataset;

  const series = [...new Set(normalized.map((row) => row.series).filter(Boolean))] as string[];
  const spansYears = series.length > 1 && series.every((name) => YEAR.test(name));
  const years = spansYears ? [...series].sort() : [];
  // A year names when a figure was taken, never what it counts, so the measure is the first
  // series that is not one - `unit` when every series is a year, or there are none.
  const measure = series.find((name) => !YEAR.test(name)) ?? unit;

  const facts: { value: string; label: string }[] = [];

  if (spansYears) {
    facts.push({
      value: String(new Set(normalized.map((row) => row.label)).size),
      label: t('analysisResult.statistaCharts.seriesTracked'),
    });
  }
  if (measure) {
    facts.push({ value: measure, label: t('analysisResult.statistaCharts.measure') });
  }
  const published = formatPublished(publishedDate);
  if (published) {
    facts.push({ value: published, label: t('analysisResult.statistaCharts.published') });
  }

  if (normalized.length > 0) {
    facts.push({
      value: spansYears
        ? `${years[0]}–${years[years.length - 1]}`
        : t('analysisResult.statistaCharts.itemCount', { count: normalized.length }),
      label: t('analysisResult.statistaCharts.coverage'),
    });
  }

  if (facts.length === 0) return null;

  return (
    <div className="flex min-w-0 flex-col justify-center gap-[18px] border-border-light md:h-full md:border-l md:pl-6">
      {facts.map((fact) => (
        <div key={fact.label} className="flex flex-col">
          <span className="text-heading-lg font-medium leading-tight tracking-tight text-foreground tabular-nums text-balance">
            {fact.value}
          </span>
          <span className="mt-[2px] text-body-sm leading-snug text-foreground-subtle">
            {fact.label}
          </span>
        </div>
      ))}
    </div>
  );
};
